import { GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { QueryClient, dehydrate } from 'react-query';

import { getMinisiteOverview } from '@/services/company/company.repo';
import { buildMarketplaceRedirect } from '@/services/company/company.redirect';
import { CompanyOverviewResponse } from '@/services/company';
import { ensureKeepAlive } from '@/infra/http/server-agent';

export const AGENDAPRO_REDIRECT = {
  redirect: {
    permanent: false,
    destination: 'https://agendapro.com/',
  },
} as const;

type Resolved = {
  companyOverview: CompanyOverviewResponse;
  companySlug?: string;
  queryClient: QueryClient;
};

export const withMinisiteOverview =
  <P extends { [key: string]: any }>(
    handler: (ctx: GetServerSidePropsContext, resolved: Resolved) => Promise<GetServerSidePropsResult<P>>,
  ): GetServerSideProps<P> =>
  async (ctx) => {
    ensureKeepAlive();

    const companySlug = ctx.req.headers.host?.split('.')[0];
    const { countryCode } = ctx.query as { countryCode: string };
    const locationId = (ctx.params as { locationId?: string } | undefined)?.locationId;
    const queryClient = new QueryClient();

    try {
      const { redirect, ...companyOverview } = await getMinisiteOverview(companySlug, countryCode, locationId);
      const marketplaceRedirect = buildMarketplaceRedirect(ctx, redirect);

      if (marketplaceRedirect) {
        return marketplaceRedirect;
      }

      queryClient.setQueryData(
        ['getCompanyOverview', companySlug, countryCode, ...(locationId ? [locationId] : [])],
        companyOverview,
      );

      return handler(ctx, { companyOverview, companySlug, queryClient });
    } catch (error) {
      console.error(
        JSON.stringify({
          action: 'MINISITE_OVERVIEW_FAILED',
          companySlug,
          countryCode,
          locationId,
          message: error instanceof Error ? error.message : String(error),
        }),
      );

      return AGENDAPRO_REDIRECT;
    }
  };

type Options = {
  /** Query params to forward as top-level props (e.g. ['confirmationCode']). */
  forwardQuery?: readonly string[];
  /** When true, includes `companySlug` in the props returned to the page. */
  includeSlug?: boolean;
};

/**
 * Shared getServerSideProps factory for entry pages that only need the
 * company overview prefetched.
 */
export const companyOverviewGssp = ({ forwardQuery = [], includeSlug = true }: Options = {}): GetServerSideProps =>
  withMinisiteOverview(async (ctx, { companyOverview, companySlug, queryClient }) => {
    const query = ctx.query as Record<string, string>;
    const forwarded = forwardQuery.reduce<Record<string, string>>((acc, key) => {
      // Skip undefined: Next.js no serializa `undefined` en props, así que los
      // query params opcionales (ej. autoCancel) deben omitirse cuando no vienen.
      if (query[key] !== undefined) {
        acc[key] = query[key];
      }
      return acc;
    }, {});

    return {
      props: {
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        companyOverview,
        ...(includeSlug ? { companySlug } : {}),
        ...forwarded,
      },
    };
  });
